// Army stacks on the map: tank, gun and infantry models standing on sourced front lines, in the
// holder's colour. Symbols of "forces on this line", not of their number.
//
//   UnitStack[] (stacks.ts) ─┐
//   camera height ─► fade ───┼─► project ─► on screen ─► selectStacks ─► one InstancedMesh per model kind
//   HexShapes ─► ground y ───┘   (px)                  (spacing, caps)   (rewritten only when the set changes)
//
// Team parts take the actor's colour from the actors texture, by a per-instance actor index; the
// holder's cluster and the foe's each carry their own.
//
// On the flat strategic map the models are hidden and each cluster is one ink icon instead
// (unitIcons.ts); the stacks, their fronts and their sources are the same.

import * as THREE from 'three/webgpu';
import { attribute, mix, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { WORLD_W, hexAtWorld, hexCenter, hexCol, hexRow } from '../../core/grid.ts';
import type { ViewMode } from '../api.ts';
import type { ViewCore } from '../index.ts';
import { wrapDelta } from '../camera.ts';
import type { HexData } from '../hexdata.ts';
import { PaintedMaterial, createPaintStyle, type PaintStyle } from '../lighting.ts';
import { loadAssets } from '../sites/geometry.ts';
import { screenNode } from '../tsl/color.ts';
import { HEX_KIND, hexTopHeight, type HexShapes } from '../terrain/heights.ts';
import { crownProfile } from '../terrain/mesher.ts';
import { placeStack, selectStacks, type Sighting, type UnitKind, type UnitStack } from './stacks.ts';
import { ICON_CAPACITY, ICON_HALF, iconSlot, placeIcons } from './unitIcons.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** URL of public/assets: the view's default (src/view/index.ts DEFAULT_ASSETS). */
const ASSET_BASE = '/assets';
const ASSET: Readonly<Record<UnitKind, string>> = {
  tank: 'unit.tank',
  artillery: 'unit.artillery',
  infantry: 'unit.infantry',
};

const KINDS = Object.keys(ASSET) as UnitKind[];
const CAPACITY = 1024;
const ROUGHNESS = 0.8;
const UNIT_RIM = 0.04;
const UNIT_SPECULAR = 0.04;
const TEAM_GAIN = 1.15; // team parts are authored ~0.82 grey; lift the owner colour back
const NO_ACTOR = 0;

// Size: at rest a stack is UNIT_SCALE model units; farther out it grows so it stays legible, then
// fades out between FADE_FROM and FADE_TO (camera height).
const UNIT_SCALE = 1.7;
const GROW_FROM = 8;
const GROW_TO = 60;
const GROW_MAX = 2.2;
const FADE_FROM = 80;
const FADE_TO = 140;
const SCALE_STEPS = 16; // scale changes are quantised so the rewrite reruns a few times per zoom
const OFF_SCREEN = 1.1; // clip-space bound (1 = the screen edge): a stack a little off screen still counts
const HALF = 0.5;
const GROUND_SINK = 0.02;
const AMP_SINK = 1;
const LIFT = 0.01;
const ICON_LIFT = 0.02; // an icon lies flat on the ground: clear of the noise the model feet sink into
const HASH_SEED = 2166136261;
const HASH_PRIME = 16777619;

/** Ground height at local offset (lx, lz) of a land hex (features/cities.ts groundAt). */
function groundAt(shapes: HexShapes, t: number, lx: number, lz: number): number {
  const r = Math.min(1, Math.hypot(lx, lz));
  const crown = shapes.crown[t] * shapes.crownScale[t] * crownProfile(r);
  return shapes.base[t] + shapes.lift[t] + crown - GROUND_SINK - AMP_SINK * shapes.amp[t];
}

function smooth(from: number, to: number, v: number): number {
  const t = Math.min(1, Math.max(0, (v - from) / (to - from)));
  return t * t * (3 - 2 * t);
}

/** Scale of every model at a camera height; 0 when the stacks are faded out. */
export function unitScale(height: number): number {
  const grow = 1 + (GROW_MAX - 1) * smooth(GROW_FROM, GROW_TO, height);
  const fade = 1 - smooth(FADE_FROM, FADE_TO, height);
  return Math.round(UNIT_SCALE * grow * fade * SCALE_STEPS) / SCALE_STEPS;
}

/** Column-major T · R_y(yaw) · S(s) into `out` at `at` (features/layer.ts writeMatrix). */
function writeMatrix(out: Float32Array, at: number, x: number, y: number, z: number, yaw: number, s: number): void {
  const c = Math.cos(yaw);
  const sn = Math.sin(yaw);
  out.fill(0, at, at + 16);
  out[at] = c * s;
  out[at + 2] = -sn * s;
  out[at + 5] = s;
  out[at + 8] = sn * s;
  out[at + 10] = c * s;
  out[at + 12] = x;
  out[at + 13] = y;
  out[at + 14] = z;
  out[at + 15] = 1;
}

/** Size of the map on screen, in CSS pixels. */
export interface Screen {
  width: number;
  height: number;
}

interface Slot {
  mesh: THREE.InstancedMesh;
  actor: THREE.InstancedBufferAttribute;
}

export class UnitLayer {
  readonly group = new THREE.Group();
  private readonly shapes: HexShapes;
  private readonly data: HexData;
  private readonly style: PaintStyle;
  private readonly slots = new Map<UnitKind, Slot>();
  private readonly icons: Slot;
  private mode: ViewMode = '3d';
  private stacks: UnitStack[] = [];
  private tops: number[] = [];
  private readonly byHex = new Map<number, UnitStack>();
  private shown: UnitStack[] = [];
  private readonly projView = new THREE.Matrix4();
  private readonly clip = new THREE.Vector4();
  private signature = -1;

  private constructor(core: ViewCore) {
    this.shapes = core.shapes;
    this.data = core.data;
    this.style = createPaintStyle();
    this.style.rimStrength.value = UNIT_RIM;
    this.style.specular.value = UNIT_SPECULAR;
    this.group.name = 'political:units';
    this.icons = iconSlot(core.data);
    this.group.add(this.icons.mesh);
  }

  /** Load the models; a missing asset leaves its kind undrawn (logged). */
  static async create(core: ViewCore): Promise<UnitLayer> {
    const layer = new UnitLayer(core);
    const assets = await loadAssets(ASSET_BASE, Object.values(ASSET));
    for (const kind of KINDS) {
      const geometry = assets.get(ASSET[kind]);
      if (geometry) {
        layer.addSlot(kind, geometry);
      }
    }
    return layer;
  }

  /** Replace the stacks. Water hexes get none: a front over a strait or lake stays a ribbon. */
  setStacks(stacks: readonly UnitStack[]): void {
    this.stacks = stacks.filter((s) => this.shapes.kind[s.hex] === HEX_KIND.LAND);
    this.tops = this.stacks.map((s) => hexTopHeight(this.shapes, s.hex));
    this.shown = [];
    this.byHex.clear();
    this.signature = -1;
  }

  /** Every candidate stack (those on land): what could be drawn. */
  all(): readonly UnitStack[] {
    return this.stacks;
  }

  /** The stacks drawn now: the candidates the screen has room for. */
  drawn(): readonly UnitStack[] {
    return this.shown;
  }

  stackAt(hex: number): UnitStack | null {
    return this.byHex.get(hex) ?? null;
  }

  /** Show one of each model at (x, y, z) so its pipeline compiles now, not at first zoom. */
  warm(x: number, y: number, z: number): void {
    for (const s of this.slots.values()) {
      writeMatrix(s.mesh.instanceMatrix.array as Float32Array, 0, x, y, z, 0, 1);
      s.actor.setX(0, NO_ACTOR);
      s.mesh.count = Math.max(s.mesh.count, 1);
      s.mesh.visible = true;
      s.mesh.instanceMatrix.needsUpdate = true;
      s.actor.needsUpdate = true;
    }

    // The icons compile now too: the first switch to the strategic map must not stall on it.
    writeMatrix(this.icons.mesh.instanceMatrix.array as Float32Array, 0, x, y, z, 0, 1);
    this.icons.actor.setX(0, NO_ACTOR);
    this.icons.mesh.count = Math.max(this.icons.mesh.count, 1);
    this.icons.mesh.visible = true;
    this.icons.mesh.instanceMatrix.needsUpdate = true;
    this.icons.actor.needsUpdate = true;

    // No real signature equals this, so the next update() rewrites every slot.
    this.signature = -1;
  }

  /** Models in 3D, ink icons on the strategic map: the next update() rewrites for the mode. */
  setMode(mode: ViewMode): void {
    if (mode === this.mode) {
      return;
    }

    this.mode = mode;
    this.signature = -1;
  }

  /** Choose and write this frame's instances; rewrites only when the chosen set changes. */
  update(camera: THREE.Camera, camX: number, height: number, screen: Screen): void {
    const scale = unitScale(height);
    if (scale === 0 || this.stacks.length === 0) {
      this.hide();
      return;
    }

    camera.updateMatrixWorld();
    this.projView.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);

    // Candidates on screen, in plan order (central first), with their pixel positions.
    const seen: Array<Sighting & { x: number; index: number }> = [];
    this.stacks.forEach((stack, index) => {
      const x = camX + wrapDelta(camX, stack.x);
      this.clip.set(x, this.tops[index], stack.z, 1).applyMatrix4(this.projView);
      const w = this.clip.w;
      if (w <= 0 || Math.abs(this.clip.x) > w * OFF_SCREEN || Math.abs(this.clip.y) > w * OFF_SCREEN) {
        return;
      }

      seen.push({ stack, x, index, px: (this.clip.x / w + 1) * HALF * screen.width, py: (1 - this.clip.y / w) * HALF * screen.height });
    });

    // Signature of the chosen set (index, wrap copy, scale step).
    const chosen = selectStacks(seen);
    let sig = Math.imul(HASH_SEED ^ Math.round(scale * SCALE_STEPS), HASH_PRIME);
    for (const c of chosen) {
      const copy = Math.round((c.x - c.stack.x) / WORLD_W) + 1;
      sig = Math.imul(sig ^ (c.index * 3 + copy), HASH_PRIME);
    }

    sig = (sig ^ chosen.length) >>> 0;
    if (sig === this.signature) {
      return;
    }
    this.signature = sig;

    this.shown = chosen.map((c) => c.stack);
    this.byHex.clear();
    for (const s of this.shown) {
      if (!this.byHex.has(s.hex)) {
        this.byHex.set(s.hex, s);
      }
    }
    this.write(chosen, scale);
  }

  dispose(): void {
    for (const s of this.slots.values()) {
      s.mesh.geometry.dispose();
      (s.mesh.material as THREE.Material).dispose();
      s.mesh.dispose();
    }
    this.slots.clear();
    this.icons.mesh.geometry.dispose();
    (this.icons.mesh.material as THREE.Material).dispose();
    this.icons.mesh.dispose();
    this.group.clear();
  }

  private hide(): void {
    // Forget the signature: the next visible frame must rewrite.
    this.signature = -1;
    this.shown = [];
    this.byHex.clear();
    for (const s of this.slots.values()) {
      s.mesh.visible = false;
    }
    this.icons.mesh.visible = false;
  }

  private write(chosen: ReadonlyArray<{ stack: UnitStack; x: number }>, scale: number): void {
    if (this.mode === 'strategic') {
      this.writeIcons(chosen, scale);
      return;
    }

    this.icons.mesh.visible = false;
    const fill = new Map<UnitKind, number>();

    for (const { stack, x } of chosen) {
      const shift = x - stack.x;
      for (const m of placeStack(stack, scale)) {
        const slot = this.slots.get(m.kind);
        const i = fill.get(m.kind) ?? 0;
        if (!slot || i >= CAPACITY) {
          continue;
        }

        // Only members on land: a flank that would hang over water is left out.
        const hex = this.hexOf(m.x, m.z);
        if (this.shapes.kind[hex.t] !== HEX_KIND.LAND) {
          continue;
        }

        fill.set(m.kind, i + 1);
        const y = groundAt(this.shapes, hex.t, hex.lx, hex.lz) + LIFT * scale;
        writeMatrix(slot.mesh.instanceMatrix.array as Float32Array, i * 16, m.x + shift, y, m.z, m.yaw, scale);
        slot.actor.setX(i, m.actor);
      }
    }

    for (const [kind, slot] of this.slots) {
      this.commit(slot, fill.get(kind) ?? 0);
    }
  }

  /** The strategic map: one icon per cluster, the models hidden. */
  private writeIcons(chosen: ReadonlyArray<{ stack: UnitStack; x: number }>, scale: number): void {
    for (const s of this.slots.values()) {
      s.mesh.visible = false;
    }

    let n = 0;
    for (const { stack, x } of chosen) {
      const shift = x - stack.x;
      for (const icon of placeIcons(stack, scale)) {
        // Only on land, as the models: an icon that would hang over water is left out.
        const hex = this.hexOf(icon.x, icon.z);
        if (n >= ICON_CAPACITY || this.shapes.kind[hex.t] !== HEX_KIND.LAND) {
          continue;
        }

        const y = groundAt(this.shapes, hex.t, hex.lx, hex.lz) + GROUND_SINK + (1 + AMP_SINK) * this.shapes.amp[hex.t] + ICON_LIFT;
        writeMatrix(this.icons.mesh.instanceMatrix.array as Float32Array, n * 16, icon.x + shift, y, icon.z, icon.yaw, scale * ICON_HALF);
        this.icons.actor.setX(n, icon.actor);
        n++;
      }
    }

    this.commit(this.icons, n);
  }

  /** Show the first `n` instances of a slot, uploading only those. */
  private commit(slot: Slot, n: number): void {
    slot.mesh.count = n;
    slot.mesh.visible = n > 0;
    if (n === 0) {
      return;
    }

    slot.mesh.instanceMatrix.clearUpdateRanges();
    slot.mesh.instanceMatrix.addUpdateRange(0, n * 16);
    slot.mesh.instanceMatrix.needsUpdate = true;
    slot.actor.clearUpdateRanges();
    slot.actor.addUpdateRange(0, n);
    slot.actor.needsUpdate = true;
  }

  /** Hex under a world point and the point's offset from its centre. */
  private hexOf(x: number, z: number): { t: number; lx: number; lz: number } {
    const t = hexAtWorld(x, z);
    const c = hexCenter(hexCol(t), hexRow(t));
    return { t, lx: wrapDelta(c.x, x), lz: z - c.z };
  }

  private addSlot(kind: UnitKind, geometry: THREE.BufferGeometry): void {
    const actor = new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY), 1);
    actor.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('iActor', actor);

    const mesh = new THREE.InstancedMesh(geometry, this.material(), CAPACITY);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
    mesh.visible = false;
    mesh.frustumCulled = false;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = `political:unit.${kind}`;
    this.group.add(mesh);
    this.slots.set(kind, { mesh, actor });
  }

  /** Team parts in the actor's colour; no colour where the front's actor is unknown. */
  private material(): PaintedMaterial {
    const m = new PaintedMaterial(this.style, { roughness: ROUGHNESS, metalness: 0 });
    const vertexColor = attribute('color', 'vec3') as V3;
    const team = attribute('_team', 'float') as unknown as F;
    // Rounded: the varying interpolates a hair off the integer index and would flicker between actors.
    const actor = (attribute('iActor', 'float') as unknown as F).round();
    const known = actor.greaterThan(NO_ACTOR).select(team, 0);
    const owner = this.data.actorColor(actor, 0);
    m.colorNode = screenNode(vertexColor).mul(mix(vec3(1), owner.mul(TEAM_GAIN), known));
    return m;
  }
}
